import * as React from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from 'core/i18n'
import { IJsonResponse } from 'core/JsonApiSerializer'
import { troubleReportService, utilService } from 'core/services'
import { Button, InputTextArea, Select, Panel } from 'components/atoms'
import { IWindow } from 'static/interfaces'
import SidebarLayout from 'components/pages/_layouts/sidebarLayout'
import moment from 'moment'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: IWindow

interface IProps {
  trouble: IJsonResponse
  statuses: any
  user: any
}

const TroubleReportShow: React.FC<IProps> = props => {
  const { data: troubleReport } = utilService.getDataFromJson(props.trouble)
  const { data: user } = utilService.getDataFromJson(props.user)
  const [status, setStatus] = React.useState(troubleReport.status)
  const [memo, setMemo] = React.useState(troubleReport.memo || '')
  const [saving, setSaving] = React.useState<boolean>(false)

  const handleSubmit = React.useCallback(
    async e => {
      e.preventDefault()
      setSaving(true)
      try {
        const { flush } = await troubleReportService.updateTrouble(troubleReport, {
          status,
          memo,
        })
        window.flashMessages.addMessage({ text: flush.message, type: flush.type })
        location.href = '/agent/trouble_reports'
      } catch (e) {
        console.error(e)
      }
      setSaving(false)
    },
    [troubleReport, status, memo]
  )

  return (
    <SidebarLayout user={user}>
      <S.troubleReportShow>
        <Panel title="トラブル報告 詳細">
          <div className="info">
            <div>
              <span className="infoTitle">トラブル報告ID:</span> {troubleReport.id}
            </div>
            {troubleReport.tour && (
              <div>
                <span className="infoTitle">ツアー日時:</span>{' '}
                {/* start_at はサーバー側で I18n.l 済みのため ja ロケールでは moment がパースできない。
                    ロケール非依存の day / start_time から組み立てる */}
                {moment(
                  `${troubleReport.tour.day} ${troubleReport.tour.start_time}`,
                  'YYYY-MM-DD HH:mm'
                ).format('YYYY-MM-DD(ddd) HH:mm')}
              </div>
            )}
            {troubleReport.tour?.post && (
              <div>
                <span className="infoTitle">ツアー名:</span>{' '}
                <a
                  href={`/agent/tours/weekly?start=${troubleReport.tour?.day}&tour_id=${troubleReport.tour_id}`}
                >
                  {troubleReport.tour.post.short_name}
                </a>
              </div>
            )}
            {troubleReport.guide && (
              <div>
                <a href={`/agent/guides/${troubleReport.guide_id}/edit`}>
                  <span className="infoTitle">ガイド:</span> {troubleReport.guide.username}
                </a>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            {troubleReport.created_at && (
              <div className="item">
                <div className="itemTitle">報告日時</div>
                <div>{moment(troubleReport.created_at).format('YYYY-MM-DD(ddd) HH:mm')}</div>
              </div>
            )}

            <div className="item">
              <div className="itemTitle">トラブル内容</div>
              <div className="reportText">{troubleReport.trouble_type_i18n || '-'}</div>
            </div>

            {troubleReport.guest_diff &&
              (() => {
                const { reservation: r, reported: rp } = troubleReport.guest_diff
                const d = {
                  adults: r.adults !== rp.adults,
                  children: r.children !== rp.children,
                  infants: r.infants !== rp.infants,
                }
                return (
                  <div className="item guestDiffItem">
                    <div className="itemTitle">
                      ゲスト人数
                      <br />
                      (大人 / 子供 / 幼児)
                    </div>
                    <div className="guestDiffValues">
                      <div className="guestDiffRow">
                        <span className="guestDiffRowLabel">予約:</span>
                        <span className={d.adults ? 'diff-bold' : ''}>{r.adults}</span> /{' '}
                        <span className={d.children ? 'diff-bold' : ''}>{r.children}</span> /{' '}
                        <span className={d.infants ? 'diff-bold' : ''}>{r.infants}</span>
                      </div>
                      <div className="guestDiffRow">
                        <span className="guestDiffRowLabel">報告:</span>
                        <span className={d.adults ? 'diff' : ''}>{rp.adults}</span> /{' '}
                        <span className={d.children ? 'diff' : ''}>{rp.children}</span> /{' '}
                        <span className={d.infants ? 'diff' : ''}>{rp.infants}</span>
                      </div>
                    </div>
                  </div>
                )
              })()}

            <div className="item">
              <div className="itemTitle">
                ステータス <span className="required">*</span>
              </div>
              <Select
                required={true}
                name="status"
                options={Object.keys(props.statuses).map(key => ({
                  label: props.statuses[key],
                  value: key,
                }))}
                defaultValue={status}
                onChangeHandler={e => setStatus(e.target.value)}
              />
            </div>

            <div className="item">
              <div className="itemTitle">メモ</div>
              <InputTextArea
                required={false}
                name="memo"
                placeholder="対応メモを入力してください"
                defaultValue={memo}
                onChangeHandler={e => setMemo(e.target.value)}
              />
            </div>

            <S.Buttons>
              <div>
                <Button>
                  <a href="/agent/trouble_reports">
                    <SvgIcon
                      name="keyboard_arrow_left"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                    {I18n.t('generic.back_to_index')}
                  </a>
                </Button>
              </div>
              <div>
                <Button primary={true} disabled={saving}>
                  {I18n.t(saving ? 'generic.saving' : 'generic.save')}
                </Button>
              </div>
            </S.Buttons>
          </form>
        </Panel>
      </S.troubleReportShow>
    </SidebarLayout>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.troubleReportShow = styled.div`
  padding: 12px 0;
  max-width: 880px;
  margin: 0 auto;

  .info {
    display: flex;
    gap: 10px;
    font-size: 14px;
    margin-bottom: 30px;
    background: #f5f5f5;
    padding: 10px;
    border-radius: 6px;

    .infoTitle {
      color: gray;
      font-size: 12px;
    }
  }

  .item {
    display: flex;
    align-items: center;
    margin: 12px 0;

    .itemTitle {
      font-size: 14px;
      font-weight: bold;
      width: 180px;

      .required {
        color: #ba3b4a;
      }
    }

    .reportText {
      flex: 1;
      white-space: pre-wrap;
      word-break: break-word;
      font-size: 14px;
    }

    .guestDiffValues {
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-size: 14px;
      .guestDiffRow {
        display: flex;
        gap: 8px;
        .guestDiffRowLabel {
          color: gray;
          width: 40px;
        }
        span {
          &.diff-bold {
            font-weight: bold;
          }
          &.diff {
            font-weight: bold;
            color: #ba3b4a;
          }
        }
      }
    }

    .InputTextArea {
      flex: 1;
    }
  }
`

S.Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 10px;
`

export default TroubleReportShow
